import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import MeetStevie from "@/components/MeetStevie";
import ComplianceOsMockup from "@/components/ComplianceOsMockup";

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      "@id": "https://calmbreeze.co.uk/about#page",
      "url": "https://calmbreeze.co.uk/about",
      "name": "About Calmbreeze — Commercial HVAC West London",
      "isPartOf": { "@id": "https://calmbreeze.co.uk/#website" },
      "about": { "@id": "https://calmbreeze.co.uk/#org" },
      "mainEntity": { "@id": "https://calmbreeze.co.uk/#stevie-coles" }
    },
    {
      "@type": "Person",
      "@id": "https://calmbreeze.co.uk/#stevie-coles",
      "name": "Stevie Coles",
      "jobTitle": "Founder & Director of HVAC Operations",
      "worksFor": { "@id": "https://calmbreeze.co.uk/#org" },
      "founderOf": { "@id": "https://calmbreeze.co.uk/#legal-entity" },
      "url": "https://calmbreeze.co.uk/about#meet-stevie",
      "sameAs": [
        "https://www.linkedin.com/in/stevie-coles-calmbreeze"
      ],
      "knowsAbout": [
        "Commercial HVAC",
        "Fire Damper Drop Testing (BS 9999)",
        "TR/19 Kitchen Extract Cleaning",
        "F-Gas Regulations",
        "TM44 Air Conditioning Inspections",
        "MVHR Ventilation Systems",
        "Steam Plant Maintenance",
        "Health & Safety Compliance"
      ],
      "hasCredential": [
        { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "F-Gas Category 1 (Level 2)" },
        { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "IOSH" },
        { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "Legionella Management for Water Systems (WH001)" },
        { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "Refrigerant Handler" },
        { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "Fire Marshal Training" }
      ]
    }
  ]
};

const Tile = ({ year, title, body }) => (
  <Reveal className="grid grid-cols-12 gap-6 py-10 border-t border-black/8">
    <div className="col-span-3 md:col-span-2">
      <div className="font-mono text-[11px] tracking-overline uppercase text-muted">{year}</div>
    </div>
    <div className="col-span-9 md:col-span-10">
      <h3 className="font-display font-semibold text-[1.4rem] md:text-[1.6rem] tracking-tight mb-3">{title}</h3>
      <p className="text-[15px] text-muted-foreground leading-relaxed max-w-2xl">{body}</p>
    </div>
  </Reveal>
);

const About = () => {
  return (
    <>
      <Seo
        title="About Calmbreeze — Founded by Stevie Coles, West London HVAC"
        description="Meet Stevie Coles, founder of Calmbreeze — 11+ years in commercial HVAC across London universities, hospitals and production sites. Learn how our Compliance OS keeps your assets audit-ready."
        path="/about"
        jsonLd={aboutJsonLd}
      />
      <section className="pt-[160px] pb-20 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-6">
          [ About · Calmbreeze ]
        </div>
        <h1 className="font-display font-bold text-[3rem] md:text-[5rem] leading-[0.98] tracking-tightest max-w-4xl">
          A West London HVAC studio,
          <span className="text-brand"> built on the human side of clean air.</span>
        </h1>
      </section>

      <section className="py-12 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <img
              src="/hero/engineer-work.jpg"
              alt="Engineer at work"
              className="w-full aspect-[4/5] object-cover rounded-md"
            />
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4">[ Mission ]</div>
            <h2 className="font-display font-bold text-[2rem] md:text-[2.5rem] tracking-tightest leading-tight mb-6">
              Making clean, fresh air accessible for everyone.
            </h2>
            <p className="text-[17px] text-muted-foreground leading-relaxed mb-8">
              We’re a small, sharp team founded in West London in 2022. We started Calmbreeze because we saw a
              gap between how facilities <em>should</em> be managed and how the HVAC industry actually delivers.
              Reactive maintenance. Missed paperwork. Different contractors for every part of the building.
            </p>
            <p className="text-[17px] text-muted-foreground leading-relaxed mb-8">
              Our answer is straightforward: a single clean-air partner that’s proactive, well-documented, and
              human to deal with. We treat client relationships as long-term partnerships, not transactions —
              and we measure ourselves on whether your systems run quietly in the background.
            </p>

            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-black/8">
              <div>
                <div className="font-mono text-brand text-[2rem] leading-none font-medium">2022</div>
                <div className="text-[11px] tracking-overline uppercase text-muted font-mono mt-2">Founded</div>
              </div>
              <div>
                <div className="font-mono text-brand text-[2rem] leading-none font-medium">W. London</div>
                <div className="text-[11px] tracking-overline uppercase text-muted font-mono mt-2">Headquartered</div>
              </div>
              <div>
                <div className="font-mono text-brand text-[2rem] leading-none font-medium">B2B</div>
                <div className="text-[11px] tracking-overline uppercase text-muted font-mono mt-2">Commercial focus</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4">[ Vision ]</div>
        <h2 className="font-display font-bold text-[2rem] md:text-[3rem] tracking-tightest leading-[1.05] max-w-4xl mb-12">
          To be the most trusted and innovative clean-air specialists —
          <span className="text-muted-foreground"> setting the benchmark for HVAC across West London and beyond.</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-2">
          {[
            ["Proactive", "We fix problems before they arrive — not after they cost you a service."],
            ["Reliable", "We show up on time, communicate clearly, and deliver consistent quality."],
            ["Partnership", "We work with our clients, not just for them — long-term relationships built on trust."],
            ["Innovation", "Modern technology, time-tested methods, no compromise on either."],
            ["Human", "Real people, real conversations. No call-centre runaround."],
            ["Compliance", "Documented to the standard your insurer and fire risk assessor expect."],
          ].map(([k, v], i) => (
            <Reveal key={k} delay={i * 60} className="border-t border-black/8 py-6">
              <div className="font-display font-semibold text-[1.15rem] mb-2 tracking-tight">{k}</div>
              <div className="text-[14px] text-muted-foreground leading-relaxed">{v}</div>
            </Reveal>
          ))}
        </div>
      </section>

      <MeetStevie />

      <ComplianceOsMockup />

      <section className="py-24 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-6">[ Story ]</div>
        <h2 className="font-display font-bold text-[2rem] md:text-[2.75rem] tracking-tightest leading-tight mb-12">
          A short history.
        </h2>
        <div>
          <Tile year="2022" title="Founded in West London" body="A small team with deep HVAC and FM experience set out to build the kind of partner they always wanted: proactive, transparent, and genuinely accountable." />
          <Tile year="2023" title="First commercial partnerships" body="Onboarded our first universities, hotels and restaurants. Delivered TR19 kitchen extract programmes and built our compliance reporting from the ground up." />
          <Tile year="2024" title="University of Reading rollout" body="Surveyed, tested and mapped 560 fire dampers across 77 buildings — a five-month, three-campus engagement delivered through term time without disruption." />
          <Tile year="2025" title="Net Zero commitment" body="Published our PPN 06/21-aligned Carbon Reduction Plan, committing to Net Zero by 2050 with interim reductions and supplier engagement." />
        </div>
      </section>

      {/* Section dividers backgrounds */}
      <section className="py-24 bg-paper-cool">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12 text-center">
          <h3 className="font-display font-bold text-[2rem] md:text-[2.5rem] tracking-tightest leading-tight mb-6">
            Want to work with us?
          </h3>
          <Link to="/contact" data-testid="about-cta-contact" className="inline-flex items-center gap-2 px-7 py-4 bg-accent text-white font-semibold text-[14px] rounded-sm hover:bg-brand transition-colors">
            Get in touch <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default About;
